import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { loadHead } from '../geometry/HeadGeometryLoader';
import { FacialRig, IdleExpression } from '../geometry/FacialRig';
import { ParticleEngine, AMBIENT_CAPACITY } from '../particles/ParticleEngine';
import { HeadTracking } from '../animation/HeadTracking';
import { StateMachine, AvatarState } from '../animation/StateMachine';
import { SpeechController } from '../voice/SpeechController';
import { AIAdapter, LocalMockAdapter, ProductContext, demoProduct } from '../ai/AIAdapter';
import { SpeechAdapter } from '../voice/SpeechAdapter';
import { LipSyncController } from '../voice/LipSyncController';
export interface AvatarConfig {
    modelUrl?: string;
    theme?: 'light' | 'dark' | 'adaptive';
    contrast?: 'light' | 'dark';
    particleCount?: number;
    tracking?: boolean;
    voice?: boolean;
    idleExpression?: IdleExpression;
    mode?: 'floating' | 'presentation';
    color?: string;
    forceNoWebGL?: boolean;
    aiAdapter?: AIAdapter;
    speechAdapter?: SpeechAdapter;
    streaming?: boolean;
    product?: ProductContext;
    limits?: {
        yaw: number;
        pitch: number;
        roll: number;
    };
}
export class ParticleAIAssistant extends EventTarget {
    renderer?: T.WebGLRenderer;
    scene = new T.Scene();
    camera = new T.PerspectiveCamera(35, 1, .01, 100);
    group = new T.Group();
    mesh?: T.Mesh;
    rig?: FacialRig;
    particles?: ParticleEngine;
    orbit?: OrbitControls;
    tracking = new HeadTracking();
    state = new StateMachine();
    speech: SpeechAdapter = new SpeechController();
    product = demoProduct;
    adapter: AIAdapter = new LocalMockAdapter();
    host?: HTMLElement;
    config: AvatarConfig = {};
    ready: Promise<void> = Promise.resolve();
    private speechAbort = new AbortController();
    private demoLip = new LipSyncController();
    private abort = new AbortController();
    private observer?: ResizeObserver;
    private request?: AbortController;
    private frame = 0;
    private previous = 0;
    private started = 0;
    private expressionTime = 0;
    private disposed = false;
    private lifecycle = 0;
    private loss = false;
    private measured = 0;
    private frames = 0;
    private fpsTime = 0;
    private autoOrbit = false;
    private inspected = false;
    private wire = false;
    private manualAngle?: T.Euler;
    private view: 'particle' | 'geometry' = 'particle';
    private neutral = new T.MeshStandardMaterial({ color: 0xaeb8c8, roughness: .87, metalness: 0 });
    private depth = new T.MeshBasicMaterial({ colorWrite: false, depthWrite: true, side: T.FrontSide, polygonOffset: true, polygonOffsetFactor: 2, polygonOffsetUnits: 2 });
    reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    fps = 0;
    density = 1;
    lodReason = 'device';
    constructor() { super(); this.state.addEventListener('change', e => this.dispatchEvent(new CustomEvent('statechange', { detail: (e as CustomEvent).detail }))); }
    private bindSpeech(adapter: SpeechAdapter) { this.speechAbort.abort(); this.speech.stop(); this.speech = adapter; this.speechAbort = new AbortController(); const signal = this.speechAbort.signal; this.speech.addEventListener('start', () => { this.setState('speaking'); this.dispatchEvent(new CustomEvent('speechstart', { detail: { silent: this.speech.silent, text: this.speech.text } })); }, { signal }); this.speech.addEventListener('end', () => { if (this.state.current === 'speaking')
        this.setState('idle'); }, { signal }); }
    initialize(host: HTMLElement, config: AvatarConfig = {}) {
        if (this.host) this.destroy();
        this.disposed=false;this.loss=false;this.previous=0;this.expressionTime=0;this.autoOrbit=false;this.inspected=false;this.manualAngle=undefined;this.view='particle';this.wire=false;this.density=1;this.brightness=1.25;this.measured=this.frames=this.fps=0;
        this.abort=new AbortController();this.host=host;this.config=config;this.product=config.product??demoProduct;this.adapter=config.aiAdapter??new LocalMockAdapter();this.bindSpeech(config.speechAdapter??new SpeechController());
        this.tracking.enabled=config.tracking??true;this.tracking.limits=config.limits??{yaw:35,pitch:22,roll:5};this.tracking.value.fill(0);this.tracking.velocity.fill(0);this.tracking.target.fill(0);this.tracking.lastActivity=0;this.tracking.speed=1;
        this.ready=this.setup(++this.lifecycle);return this.ready;
    }
    private async setup(lifecycle:number) {
        const host = this.host!;
        if (getComputedStyle(host).position === 'static')
            host.style.position = 'relative';
        try {
            if (this.config.forceNoWebGL)
                throw Error('WebGL отключён');
            this.renderer = new T.WebGLRenderer({ alpha: true, antialias: true, premultipliedAlpha: true, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
        }
        catch {
            const fallback = document.createElement('p');
            fallback.className = 'avatar-fallback';
            fallback.textContent = '3D-аватар недоступен: требуется WebGL. Текстовый диалог продолжает работать.';
            host.append(fallback);
            this.setState('error');
            this.dispatchEvent(new Event('fallback'));
            return;
        }
        const renderer = this.renderer;
        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
        renderer.domElement.setAttribute('aria-hidden', 'true');
        renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;pointer-events:none';
        host.append(renderer.domElement);
        renderer.domElement.addEventListener('webglcontextlost', e => { e.preventDefault(); this.loss = true; cancelAnimationFrame(this.frame); this.setState('error'); this.dispatchEvent(new Event('contextlost')); }, { signal: this.abort.signal });
        renderer.domElement.addEventListener('webglcontextrestored', () => { this.loss = false; this.setState('idle'); this.previous = 0; this.loop(performance.now()); }, { signal: this.abort.signal });
        let loaded:T.Mesh;
        try {
            loaded = await loadHead(this.config.modelUrl ?? 'assets/avatar-head.glb');
        }
        catch (e) {
            if(this.disposed||lifecycle!==this.lifecycle)return;
            this.setState('error');
            const p = document.createElement('p');
            p.className = 'avatar-fallback';
            p.textContent = 'Не удалось загрузить 3D-модель. Проверьте адрес ресурса.';
            host.append(p);
            this.dispatchEvent(new CustomEvent('error', { detail: e }));
            return;
        }
        if (this.disposed||lifecycle!==this.lifecycle) {
            loaded.geometry.dispose();(loaded.material as T.Material).dispose();
            return;
        }
        this.mesh=loaded;
        (this.mesh.material as T.Material).dispose();
        this.mesh.material = this.depth;
        this.mesh.renderOrder = 0;
        this.rig = new FacialRig(this.mesh, this.config.idleExpression);
        const count = this.config.particleCount ?? (innerWidth < 600 ? 14000 : innerWidth < 1024 ? 20000 : 48000);
        this.particles = new ParticleEngine(this.mesh, count);
        this.group = new T.Group();
        this.group.add(this.mesh, this.particles.points, this.particles.ambient);
        this.scene = new T.Scene();
        this.scene.add(this.group);
        const key = new T.DirectionalLight(0xffffff, 2.5);
        key.position.set(-3, 4, 5);
        const rim = new T.DirectionalLight(0xbbd2ef, 1.6);
        rim.position.set(3, 2, -3);
        this.scene.add(key, rim, new T.HemisphereLight(0xe1ecff, 0x242933, .65));
        this.camera.position.set(0, 0, 6.4);
        this.orbit = new OrbitControls(this.camera, host);
        this.orbit.enabled = false;
        this.orbit.enableDamping = true;
        this.orbit.enablePan = false;
        this.orbit.minDistance = 4.3;
        this.orbit.maxDistance = 10;
        this.orbit.target.set(0, 0, 0);
        const pointerAttention = (e: PointerEvent) => { if (!this.inspected) {
            this.tracking.pointer(e.clientX, e.clientY, host.getBoundingClientRect());
            if (this.state.current === 'idle')
                this.setState('attention');
        } };
        document.addEventListener('pointermove', pointerAttention, { signal: this.abort.signal, passive: true });
        document.addEventListener('pointerdown', pointerAttention, { signal: this.abort.signal, passive: true });
        document.addEventListener('focusin', e => {
            if (this.inspected || !(e.target instanceof HTMLElement)) return;
            const r=e.target.getBoundingClientRect();
            this.tracking.pointer(r.x+r.width/2,r.y+r.height/2,host.getBoundingClientRect());
            if(this.state.current==='idle') this.setState('attention');
        }, { signal: this.abort.signal });
        document.addEventListener('visibilitychange', () => { cancelAnimationFrame(this.frame); if (!document.hidden && !this.loss) {
            this.previous = 0;
            this.loop(performance.now());
        } }, { signal: this.abort.signal });
        this.observer = new ResizeObserver(() => this.resize());
        this.observer.observe(host);
        this.resize();
        this.setTheme(this.config.theme ?? 'dark');
        if (this.config.color)
            this.setColor(this.config.color);
        this.setProduct(this.product);
        this.setMotion(1);
        this.started = performance.now();
        this.fpsTime = this.started;
        this.setState(this.reduced ? 'idle' : 'boot');
        this.loop(this.started);
        this.dispatchEvent(new Event('ready'));
    }
    private applyDensity() { if (!this.particles)
        return; const limit = innerWidth < 600 ? 14000 : innerWidth < 1024 ? 20000 : 60000; this.particles.density(this.density * Math.min(limit, this.particles.capacity) / this.particles.capacity); this.particles.uniforms.uDevicePointSize.value=innerWidth<600?1.4:innerWidth<1024?1.18:1; }
    private resize() { if (!this.renderer || !this.host)
        return; const r = this.host.getBoundingClientRect(), w = Math.max(1, r.width), h = Math.max(1, r.height); this.renderer.setSize(w, h, false); this.camera.aspect = w / h; this.camera.updateProjectionMatrix(); if (this.particles) {
        this.particles.uniforms.uDpr.value = this.renderer.getPixelRatio();
        this.particles.uniforms.uScale.value = T.MathUtils.clamp(h / 600, .5, 1.5);
        this.particles.ambientUniforms.uDpr.value = this.renderer.getPixelRatio();
        this.particles.ambientUniforms.uScale.value = T.MathUtils.clamp(h / 600, .5, 1.5);
        this.applyDensity();
        this.dispatchEvent(new Event('diagnostics'));
    } }
    private loop = (now: number) => {
        if (this.disposed || this.loss || document.hidden || !this.renderer || !this.rig || !this.particles)
            return;
        const dt = Math.min(.035, this.previous ? (now - this.previous) / 1000 : .016);
        this.previous = now;
        const time = (now - this.started) / 1000;
        const boot = this.reduced ? 1 : T.MathUtils.smoothstep(time, 0, 1.5);
        if (boot === 1 && this.state.current === 'boot')
            this.setState('idle');
        if (this.state.current === 'attention' && now - this.tracking.lastActivity > 2400)
            this.setState('idle');
        const amplitude = this.speech.active ? this.speech.amplitude : this.state.current === 'speaking' ? this.demoLip.approximate(time) : 0;
        // Advance only on rendered frames, so returning from a hidden tab
        // resumes the expression rather than skipping to another phase.
        this.expressionTime += dt;
        this.rig.update(time, amplitude, this.tracking.value[0], this.tracking.value[1], this.reduced, this.expressionTime, !this.speech.active && this.state.current !== 'speaking');
        for (const [n, v] of Object.entries(this.speech.preciseWeights))
            this.rig.set(n, v);
        if (!this.inspected && !this.manualAngle)
            this.group.rotation.copy(this.tracking.update(dt, time, this.reduced));
        else if (this.manualAngle)
            this.group.rotation.copy(this.manualAngle);
        if (this.autoOrbit)
            this.group.rotation.y += dt * .3;
        this.orbit?.update();
        this.particles.update(time, this.rig.weights, boot, amplitude);
        this.particles.focus(this.tracking.target[0],this.tracking.target[1],this.tracking.enabled&&!this.inspected&&!this.reduced&&now-this.tracking.lastActivity<2400,dt);
        if (this.state.current === 'thinking')
            this.particles.uniforms.uBrightness.value = this.brightness * (1 + Math.sin(time * 2) * .06);
        else
            this.particles.uniforms.uBrightness.value = this.brightness;
        this.renderer.render(this.scene, this.camera);
        this.frames++;
        if (now - this.fpsTime > 1500) {
            this.fps = this.frames * 1000 / (now - this.fpsTime);
            this.frames = 0;
            this.fpsTime = now;
            this.measured++;
            if (this.fps < 28 && this.measured > 2) {
                const airCount = this.particles.ambient.geometry.drawRange.count;
                const airFloor = innerWidth < 600 ? 240 : 360;
                if(airCount > airFloor)this.particles.ambient.geometry.setDrawRange(0,Math.max(airFloor,Math.floor(airCount*.8)));
                else if(this.density>.4){this.density=Math.max(.4,this.density-.1);this.applyDensity();}
                this.lodReason = 'frame-time';
            }
            this.dispatchEvent(new Event('diagnostics'));
        }
        this.frame = requestAnimationFrame(this.loop);
    };
    brightness = 1.25;
    setState(state: AvatarState) { this.state.set(state.toLowerCase() as AvatarState); }
    setProduct(product: ProductContext) { this.product = product; if (this.speech instanceof SpeechController)
        this.speech.language = product.language ?? 'ru-RU'; }
    setTheme(theme: 'light' | 'dark' | 'adaptive') { this.config.theme = theme; const resolved = theme === 'adaptive' ? (this.config.contrast ?? 'dark') : theme; this.particles?.theme(resolved); }
    setTracking(on: boolean) { this.tracking.enabled = on; }
    setParticleDensity(v: number) { this.density = T.MathUtils.clamp(v, .1, 1); this.applyDensity(); this.lodReason = 'manual'; }
    setColor(color: string) { this.particles?.color(color); }
    setShaderDebug(mode: 'off' | 'normals' | 'depth') { if (this.particles)
        this.particles.uniforms.uDebug.value = mode === 'normals' ? 1 : mode === 'depth' ? 2 : 0; }
    setBrightness(v: number) { this.brightness = T.MathUtils.clamp(v, .2, 2.5); }
    setMotion(v: number) { this.particles?.motion(this.reduced ? 0 : T.MathUtils.clamp(v, 0, 2)); }
    setAmbientCount(v: number) { this.particles?.ambient.geometry.setDrawRange(0, T.MathUtils.clamp(Math.round(v), 0, AMBIENT_CAPACITY)); }
    setVisibility(on: boolean) { if (this.host)
        this.host.hidden = !on; cancelAnimationFrame(this.frame); if (on) {
        this.previous = 0;
        this.loop(performance.now());
    } }
    setView(view: 'particle' | 'geometry', wireframe = false) { this.view = view; this.wire = wireframe; if (!this.mesh || !this.particles)
        return; this.mesh.material = view === 'geometry' ? this.neutral : this.depth; this.neutral.wireframe = wireframe; this.particles.points.visible = view === 'particle'; this.particles.ambient.visible = view === 'particle'; }
    setInspector(on: boolean) { this.inspected = on; if (this.orbit)
        this.orbit.enabled = on; if (!on) {
        this.manualAngle = undefined;
        this.front();
    } if (this.host)
        this.host.style.touchAction = on ? 'none' : 'pan-y'; }
    setAutoOrbit(on: boolean) { this.autoOrbit = on; this.inspected = on; if (on)
        this.manualAngle = undefined; }
    setAngle(yaw: number, pitch = 0) { this.inspected = true; this.autoOrbit = false; this.manualAngle = new T.Euler(T.MathUtils.degToRad(pitch), T.MathUtils.degToRad(yaw), 0, 'YXZ'); this.camera.position.set(0, 0, 6.4); this.orbit?.target.set(0, 0, 0); this.group.rotation.copy(this.manualAngle); this.render(); }
    front() { this.autoOrbit = false; this.manualAngle = undefined; this.group.rotation.set(0, 0, 0); this.camera.position.set(0, 0, 6.4); this.orbit?.target.set(0, 0, 0); this.orbit?.update(); }
    render() { this.renderer?.render(this.scene, this.camera); }
    capture() { this.render(); return this.renderer?.domElement.toDataURL('image/png'); }
    async speak(text: string) { this.dispatchEvent(new CustomEvent('subtitle', { detail: text })); if (this.config.voice === false) {
        this.setState('idle');
        return;
    } await this.speech.speak(text); }
    stopSpeaking() { this.speech.stop(); this.request?.abort(); if (this.state.current !== 'error')
        this.setState('idle'); }
    async ask(question: string) { this.request?.abort(); this.speech.stop(); const request = new AbortController(); this.request = request; this.setState('listening'); await new Promise(r => setTimeout(r, 220)); if (request.signal.aborted)
        return; this.setState('thinking'); try {
        let answer = '';
        if (this.config.streaming && this.adapter.stream) {
            for await (const delta of this.adapter.stream(question, this.product, request.signal)) {
                if (request.signal.aborted)
                    return;
                answer += delta;
                this.dispatchEvent(new CustomEvent('answerprogress', { detail: answer }));
                this.dispatchEvent(new CustomEvent('subtitle', { detail: answer }));
            }
        }
        else
            answer = await this.adapter.ask(question, this.product, request.signal);
        if (request.signal.aborted)
            return;
        this.dispatchEvent(new CustomEvent('answer', { detail: answer }));
        await this.speak(answer);
        return answer;
    }
    catch (e) {
        if (!request.signal.aborted) {
            this.setState('error');
            this.dispatchEvent(new CustomEvent('error', { detail: e }));
            throw e;
        }
    } }
    diagnostics() { return { state: this.state.current, fps: this.fps, particles: this.particles?.points.geometry.drawRange.count ?? 0, capacity: this.particles?.capacity ?? 0, ambient: this.particles?.ambient.geometry.drawRange.count ?? 0, morphTargets: this.rig?.names, vertices: this.mesh?.geometry.attributes.position.count, triangles: (this.mesh?.geometry.index?.count ?? 0) / 3, lod: this.lodReason, webgl: !!this.renderer, disposed: this.disposed, view: this.view }; }
    destroy() { this.disposed = true;this.lifecycle++; cancelAnimationFrame(this.frame); this.abort.abort(); this.speechAbort.abort(); this.request?.abort(); this.observer?.disconnect(); this.orbit?.dispose(); this.speech.destroy(); this.particles?.dispose(); this.mesh?.geometry.dispose(); this.renderer?.dispose(); this.renderer?.forceContextLoss(); this.renderer?.domElement.remove(); this.host?.querySelectorAll('.avatar-fallback').forEach(e => e.remove()); this.neutral.dispose(); this.depth.dispose(); this.scene.clear();this.group.clear(); this.host = undefined; this.renderer = undefined; this.particles = undefined;this.mesh=undefined;this.rig=undefined;this.orbit=undefined;this.observer=undefined; this.frame = 0; }
}
